import { captureException } from '@sentry/browser';
import clevertap from 'clevertap-web-sdk';
import { useFlags } from 'launchdarkly-react-client-sdk';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import i18n from 'src/config/i18n';
import { languages } from 'src/config/i18n.config.mjs';
import { navigationService } from 'src/config/rootService';
import { Dispatch, RootState } from 'src/config/rootStore';
import { isHrAdminSelector } from 'src/modules/admin/selectors/adminSelectors';
import {
    isOnboardedInfoFetched,
    isOnboardingCompleted,
    isOnboardingFirstStepCompleted,
    isOnboardingSecondStepCompleted,
} from 'src/modules/onboarding/selectors/onboardingSelectors';
import { isUserProvidersFetchCompleted } from 'src/modules/providers/selectors/providersSelectors';
import { shouldShowReTriageFlow } from 'src/modules/reTriage/selectors/reTriageSelectors';
import {
    getUserInformation,
    isUserDataFetched,
    shouldTakeToRequestPersonalEmailAddressFlow,
    shouldTakeToRequestPersonalEmailAddressFlowRequiredOnly,
} from 'src/modules/user/selectors/userSelectors';
import { GetLastInvoicedCoachingVisitDocument } from 'src/modules/videoSessions/documents/getLastInvoicedCoachingVisit.generated';
import {
    isSessionFetchingCompleted,
    shouldShowPreSessionAssessments,
} from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { initialDataFetchReady } from 'src/shared/actions/sharedActions';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';

import { isAuthenticated } from '../selectors/authenticationSelectors';

type Props = ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const PostAuth: React.FC<Props> = props => {
    const flags = useFlags();

    const dispatch = useDispatch<Dispatch>();

    const shouldTakeToPersonalEmailAddressFlowInOnboarding = useSelector(
        shouldTakeToRequestPersonalEmailAddressFlow,
    );
    const shouldTakeToPersonalEmailAddressFlowOutsideOnboarding = useSelector(
        shouldTakeToRequestPersonalEmailAddressFlowRequiredOnly,
    );

    const usingRegistrationRevamp = flags?.useRegistrationRevamp || flags?.useMagicLink;

    const isHrAdmin = useSelector(isHrAdminSelector);

    useEffect(() => {
        // In case someone is trying to access `authenticated` url without being authenticated
        if (!props.authenticated) {
            return navigationService.navigate('/');
        }

        props.initialDataFetchReady();

        dispatch.user.fetchUserInfo().then(user => {
            window.freshpaint.identify(user.uuid);

            clevertap.onUserLogin.push({
                Site: {
                    Identity: user.uuid,
                    Email: user.email,
                    'MSG-push': true,
                },
            });

            const clevertapId = clevertap.getCleverTapID();
            if (clevertapId) {
                dispatch.user.updateUserInfo({ clevertapId });
            }

            if (!user?.languagePreferences?.length) {
                const langName =
                    Object.keys(languages).find(key => languages[key] === i18n.language) ||
                    config.defaultLanguageFullName;

                dispatch.user.updateUserInfo({ languagePreferences: [langName] });
            }

            dispatch.providers.fetchMyProviders();
        });

        dispatch.meditation.fetchMeditations();

        dispatch.goalCategories.fetchGoalsCategories();
        dispatch.goalInterests.fetchGoalInterests();

        dispatch.assessments.fetchAssessments();
        dispatch.assessments.fetchLatestScores();

        dispatch.admin.fetchAdminInfo();
        dispatch.videoSessions.fetchSessions();
        dispatch.countries.fetchCountries();
        dispatch.languages.fetchLanguages();
    }, []);

    // this will check if there is a session to rate and navigate to rate a session flow if so
    // ONLY checks for invoiced sessions with `out of the app` OR `roster only` providers
    const checkIfShouldRateSession = async () => {
        try {
            const { data } = await appClient.query({
                query: GetLastInvoicedCoachingVisitDocument,
                fetchPolicy: 'network-only',
            });

            if (!data) {
                throw new Error('GetLastInvoicedCoachingVisitQuery: no response received');
            }

            const uuid = data?.lastInvoicedCoachingVisit?.uuid;

            if (uuid) {
                navigationService.navigate('/rate-session', { session: uuid });
            }
        } catch (error) {
            captureException(error);
        }
    };

    useEffect(() => {
        if (
            !props.userCoachesFetched ||
            !props.userDataFetched ||
            !props.onboardingInfoFetched ||
            !props.coachingSessionsFetchCompleted
        ) {
            return;
        }

        let nextPage = localStorage.getItem('requestedUrl') || '/dashboard';
        let nextPageParams = queryString.parse(localStorage.getItem('requestedUrlParams') || '');

        localStorage.removeItem('requestedUrl');
        localStorage.removeItem('requestedUrlParams');

        if (props.user.role === 'MEMBER') {
            if (
                props.onboardingCompleted &&
                !props.sessionUuidForPreSessionAssessments &&
                !shouldTakeToPersonalEmailAddressFlowOutsideOnboarding &&
                !props.shouldTakeToReTriageFlow
            ) {
                checkIfShouldRateSession();
            }

            if (props.sessionUuidForPreSessionAssessments) {
                nextPage = '/pre-session-assessments-intro';
                nextPageParams = { sessionUuid: props.sessionUuidForPreSessionAssessments };
            }

            if (props.onboardingCompleted && props.shouldTakeToReTriageFlow) {
                nextPage = '/re-triage';
            }

            if (
                props.onboardingCompleted &&
                shouldTakeToPersonalEmailAddressFlowOutsideOnboarding
            ) {
                if (usingRegistrationRevamp) {
                    nextPage = '/preferred-email';
                } else {
                    nextPage = '/email-preferences';
                }
            }

            if (!props.onboardingSecondStepCompleted) {
                nextPage = flags?.hideTriageSymptoms ? '/a-few-questions' : '/onboarding-wellness';
            }

            if (!props.onboardingFirstStepCompleted) {
                if (
                    usingRegistrationRevamp ||
                    (usingRegistrationRevamp && shouldTakeToPersonalEmailAddressFlowInOnboarding)
                ) {
                    nextPage = props.user.emailConfirmed
                        ? '/completion-page'
                        : props.user.newEmailAddressNotConfirmed
                        ? '/preferred-email-sent'
                        : props.user.registrationSource === 'Single Sign On'
                        ? '/registration'
                        : '/preferred-email';
                } else {
                    nextPage = shouldTakeToPersonalEmailAddressFlowInOnboarding
                        ? '/email-preferences'
                        : '/welcome';
                }
            }

            if (nextPage.includes('/re-triage') && !props.shouldTakeToReTriageFlow) {
                nextPage = '/dashboard';
            }

            if (nextPage.includes('admin') && !isHrAdmin) {
                nextPage = '/';
            }
        }

        navigationService.navigate(nextPage, nextPageParams);
    }, [props]);

    return <CenteredSpinner />;
};

const mapStateToProps = (state: RootState) => ({
    authenticated: isAuthenticated(state),
    userDataFetched: isUserDataFetched(state),
    user: getUserInformation(state),
    onboardingInfoFetched: isOnboardedInfoFetched(state),
    onboardingCompleted: isOnboardingCompleted(state),
    onboardingFirstStepCompleted: isOnboardingFirstStepCompleted(state),
    onboardingSecondStepCompleted: isOnboardingSecondStepCompleted(state),
    userCoachesFetched: isUserProvidersFetchCompleted(state),
    coachingSessionsFetchCompleted: isSessionFetchingCompleted(state),
    shouldTakeToReTriageFlow: shouldShowReTriageFlow(state),
    sessionUuidForPreSessionAssessments: shouldShowPreSessionAssessments(state),
});

const mapDispatchToProps = () => ({
    initialDataFetchReady,
});

export default connect(mapStateToProps, mapDispatchToProps)(PostAuth);
